import './RoleStageActionFlows.scss';
import Container from 'components/molecules/Container/Container';
import NavbarWithBackLink from 'components/molecules/NavbarWithBackLink/NavbarWithBackLink';
import { HelmetAndPageAnnouncer } from 'components/organisms/HelmetAndPageAnnouncer/HelmetAndPageAnnouncer';
import TestIntro from 'components/organisms/TestIntro/TestIntro';
import {
  CoderbyteAssessmentStageActionCandidateStatus,
  SupportedLocale,
  TestStatus,
  useStartCoderbyteAssessmentMutation,
  useUserQuery,
} from 'generated/graphql';
import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Loader,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import { convertExtraTimeValue } from 'views/Test/Tests/helpers/extraTime';
import { configCodingTest } from 'views/Test/TestInfo/TestInfo.constants';
import { useRoleStageActionsFlow } from './useRoleStageActionsFlow';

/**
 * localStorage key holding the URL the coding-assessment thank-you page
 * (Coderbyte's fixed redirect target) should route back into. Set on start,
 * consumed by CodingAssessmentThankYou.
 */
export const CODING_ASSESSMENT_RETURN_URL_KEY = 'codingAssessmentReturnUrl';

/**
 * The intro to the stage's Coderbyte assessment, and the only way into it.
 *
 * Starting is one mutation rather than a link plus a mark-in-progress call, as
 * the legacy external-assessment screen did. `startCoderbyteAssessment` records
 * the start *and* returns the private link, so the two cannot diverge: a
 * candidate holding a link we have not recorded as started is one we would mint
 * a second link for, and following a fresh Coderbyte link without a session in
 * that browser restarts the assessment outright.
 *
 * The link is never rendered into the page and never stored. With `autoSubmit`
 * appended it signs its holder straight into the candidate's Coderbyte account,
 * so it is used once, to navigate, and then forgotten. Returning to this screen
 * asks for it again — the monolith hands back the same one.
 */
const RoleStageCoderbyteAssessmentIntro: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleMsgType } = useNotification();
  const { stage, loading, exiting, actionsUrl, thankYouUrl } =
    useRoleStageActionsFlow();

  const userQueryResponse = useUserQuery();
  const assessmentLocale =
    userQueryResponse.data?.User.preferredLocale.locale ||
    SupportedLocale.English;

  const [startCoderbyteAssessment] = useStartCoderbyteAssessmentMutation();

  const action = stage?.actions.coderbyteAction;

  // No Coderbyte assessment on this stage — nothing to start here.
  useEffect(() => {
    if (stage && !action) {
      navigate(actionsUrl);
    }
  }, [stage, action, navigate, actionsUrl]);

  if (loading) {
    return (
      <Container className="page-loader-wrapper">
        <Loader variant="bubbles" />
      </Container>
    );
  }

  if (exiting || !stage || !action) {
    return null;
  }

  const startAssessment = () => {
    startCoderbyteAssessment({
      variables: { id: action.id },
      onCompleted: data => {
        // Coderbyte redirects the candidate to one fixed URL per assessment;
        // this key tells that landing page where to return to within the roles
        // flow.
        localStorage.setItem(CODING_ASSESSMENT_RETURN_URL_KEY, thankYouUrl);
        window.location.href = data.startCoderbyteAssessment.inviteUrl;
      },
      onError: err => {
        handleMsgType({
          type: TNotification.error,
          message: err?.message,
        });
      },
    });
  };

  const singleTestConfig = configCodingTest(
    action.estimatedTimeSeconds,
    action.allowedTimeSeconds,
    action.timeModifierPercentage
      ? convertExtraTimeValue(action.timeModifierPercentage)
      : undefined
  );

  return (
    <Container className="TestInfoContainer">
      <NavbarWithBackLink
        to={actionsUrl}
        ariaLabel={t('assessment.returnToAllAssessmentActions')}
        label={t('assessment.actions')}
      />
      <HelmetAndPageAnnouncer pageTitle={singleTestConfig.title} />
      <TestIntro
        MainIcon={singleTestConfig.icon}
        type={singleTestConfig.key}
        assessmentLocale={assessmentLocale}
        title={singleTestConfig.title}
        notes={singleTestConfig.notes}
        testStatus={
          action.status ===
          CoderbyteAssessmentStageActionCandidateStatus.InProgress
            ? TestStatus.InProgress
            : TestStatus.NotStarted
        }
        hasPractice={false}
        onStart={startAssessment}
        testTimePerQuestion={0}
        isMobileAllowed={false}
      />
    </Container>
  );
};

export default RoleStageCoderbyteAssessmentIntro;
